import { Button, ButtonRow, PopoverConfirm } from '@timelyapp/react';
import classNames from 'classnames';
import Tooltip from 'components/Tooltip';
import type { SyntheticEvent } from 'react';
import React from 'react';
import FontAwesome from 'react-fontawesome';
import type { HourState } from 'redux/modules/hourStates.schema';
import type { HourEditorProps } from 'redux/selectors/hours.draft';
import styles from './ButtonSection.module.scss';
import { Section } from './Section';

interface Props {
  isVisible?: boolean;
  isHourSaved: boolean;
  onSubmit: (event: SyntheticEvent) => void;
  onSubmitAndComplete: (event: SyntheticEvent) => void;
  onCancel: (event: SyntheticEvent) => void;
  onRejectSuggestion: (event: SyntheticEvent) => void;
  onDelete: (event: SyntheticEvent) => void;
  standalone?: boolean;
  noPadding?: boolean;
  isTimerRunning: boolean;
  ui: HourEditorProps['ui'];
  stateId: number | null;
  states: Record<number, HourState>;
  hasHourStatesFeature: boolean;
}

export const ButtonSection: React.FC<Props> = props => {
  const { ui, standalone, isVisible, noPadding } = props;
  const allowDelete =
    ui.isEditingExistingHour &&
    ((!ui.isLocked && !ui.isDayLocked) || (ui.isLocked && ui.canUserUnlock));
  const isRejectableSuggestion = ui.isBasedOnSuggestion && !props.isHourSaved && !ui.isLoading;
  const canOnlyCancel = !ui.canManage || (ui.isLocked && !ui.canUserUnlock);

  if (canOnlyCancel) {
    return (
      <Section
        className={classNames({ [styles.isHidden]: !isVisible })}
        noBottom
        standalone={standalone}
      >
        <ButtonRow
          className={classNames(styles.container, {
            [styles.standaloneContainer]: props.standalone,
            [styles.hasNotInstalledMemory]:
              !ui.hasInstalledMemory && !ui.isActingAsOtherUser && !props.standalone,
          })}
        >
          <Button priority="low" onClick={props.onCancel}>
            Cancel
          </Button>
        </ButtonRow>
      </Section>
    );
  }

  return (
    <Section
      className={classNames({ [styles.isHidden]: !isVisible })}
      noBottom
      standalone={props.standalone}
    >
      <ButtonRow
        className={classNames(styles.container, {
          [styles.standaloneContainer]: props.standalone,
          [styles.hasNotInstalledMemory]:
            !ui.hasInstalledMemory && !ui.isActingAsOtherUser && !props.standalone,
          [styles.noPadding]: noPadding,
        })}
      >
        {isVisible && (
          <Button
            data-testid="save_button_section"
            priority="xhigh"
            onClick={props.onSubmit}
            isLoading={ui.isLoading}
            disabled={ui.isSaveDisabled}
          >
            {ui.saveButtonLabel || 'Save'}
          </Button>
        )}
        {ui.showForecastCompleteButton && (
          <Button priority="low" onClick={props.onSubmitAndComplete} disabled={ui.isSaveDisabled}>
            <span>
              {ui.saveButtonLabel} & Complete {!ui.hasEnabledTic && 'Task'}
            </span>
          </Button>
        )}
        {isRejectableSuggestion && (
          <Button priority="high" onClick={props.onRejectSuggestion} destructive>
            Reject
          </Button>
        )}
        <Button priority="low" onClick={props.onCancel} data-testid="cancel_button_section">
          Cancel
        </Button>
        <ButtonRow.Right className={styles.secondaryActions}>
          {allowDelete && (
            <PopoverConfirm
              position="left"
              zIndex={100} // see variables.scss::memoryTimelineHourLaneZIndex
              prompt="Are you sure?"
              confirmLabel="Delete"
              confirmButtonDestructive
              target={
                <Tooltip position="left" title="Delete" data-testid="delete_button_section">
                  <Button priority="low">
                    <FontAwesome name="trash" />
                  </Button>
                </Tooltip>
              }
              targetCaret={false}
              onConfirm={props.onDelete}
            />
          )}
        </ButtonRow.Right>
      </ButtonRow>
    </Section>
  );
};
